import { useMemo } from "react";
import { StatusPill, type BlogStatus } from "../blog-list/StatusPill";
import { toLocalDateTime } from "../date-utils";

export type PublishMode = "draft" | "now" | "schedule";

interface Props {
  /** Derived status of the post if saved as-is. */
  status: BlogStatus;
  mode: PublishMode;
  /** ISO publish date — used by both "now" (back-dating) and "schedule". */
  scheduledAt: string | null;
  onModeChange: (mode: PublishMode) => void;
  onScheduledAtChange: (next: string | null) => void;
}

const OPTIONS: ReadonlyArray<{
  value: PublishMode;
  label: string;
  description: string;
}> = [
  {
    value: "draft",
    label: "Save as draft",
    description: "Hidden from the public site.",
  },
  {
    value: "now",
    label: "Publish now",
    description: "Live as soon as you save. Back-date if needed.",
  },
  {
    value: "schedule",
    label: "Schedule",
    description: "Goes live automatically on the date you pick.",
  },
];

/**
 * Right-rail panel that owns the publish lifecycle. Three options —
 * draft / publish now / schedule — replace the admin's bare publishAt
 * datetime input. The current saved status is shown above so the user
 * always knows the post's live state independent of pending edits.
 */
export function StatusPanel({
  status,
  mode,
  scheduledAt,
  onModeChange,
  onScheduledAtChange,
}: Props) {
  // datetime-local needs a local string without timezone suffix.
  const localValue = useMemo(() => toLocalDateTime(scheduledAt), [scheduledAt]);

  return (
    <div className="shadow-card rounded-lg bg-paper-hi">
      <div className="hair-b border-b flex items-center justify-between px-4 py-3">
        <h3 className="text-[13px] font-semibold">Status</h3>
        <StatusPill status={status} />
      </div>
      <div className="space-y-1 p-3">
        {OPTIONS.map((opt) => {
          const active = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onModeChange(opt.value)}
              className={`flex w-full items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition ${
                active
                  ? "shadow-card bg-paper-2"
                  : "hover:bg-paper-2"
              }`}
            >
              <span
                className={`mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border ${
                  active ? "border-tide" : "border-ink-6"
                }`}
                aria-hidden
              >
                {active && (
                  <span className="block h-1.5 w-1.5 rounded-full bg-tide" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <div
                  className={`text-[13px] ${
                    active ? "font-medium text-ink" : "text-ink-2"
                  }`}
                >
                  {opt.label}
                </div>
                <div className="mt-0.5 text-[11.5px] text-ink-4">
                  {opt.description}
                </div>
              </span>
            </button>
          );
        })}

        {(mode === "schedule" || mode === "now") && (
          <div className="hair-t mt-2 border-t pl-7 pr-3 pt-3">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-ink-4">
                Publish date
              </label>
              {mode === "now" && (
                <button
                  type="button"
                  onClick={() => onScheduledAtChange(new Date().toISOString())}
                  className="text-[11px] text-tide hover:text-tide-d"
                >
                  Now
                </button>
              )}
            </div>
            <input
              type="datetime-local"
              value={localValue}
              onChange={(e) =>
                onScheduledAtChange(fromLocalDateTime(e.target.value))
              }
              className="hair mt-1.5 w-full rounded-md border bg-paper px-2.5 py-1.5 text-[12.5px] text-ink-2 focus:border-tide focus:outline-none"
            />
            {mode === "now" && (
              <p className="mt-1.5 text-[11px] text-ink-4">
                Defaults to right now. Pick a past date to back-date.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function fromLocalDateTime(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
